import type {
  WindowWithServiceTopology,
  ElementWithFullscreen,
  VideoUploadProps,
} from './VideoUpload.types';

declare let window: WindowWithServiceTopology | undefined;
declare let document: Document | undefined;

export const getStaticMediaUrl = () => {
  const staticMediaUrl =
    window?.serviceTopology?.staticMediaUrl ??
    'https://static.wixstatic.com/media';

  return staticMediaUrl.endsWith('/')
    ? staticMediaUrl.slice(0, -1)
    : staticMediaUrl;
};

type NumberKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';

type GetCurrentTimeFromKeyboardKeyOptions = {
  key: NumberKey;
  startTime: number;
  endTime: number;
};

export const getCurrentTimeFromKeyboardKey = ({
  key,
  startTime,
  endTime,
}: GetCurrentTimeFromKeyboardKeyOptions) => {
  const numberKey = Number(key);
  if (numberKey === 0) {
    return startTime;
  }

  const seekPercentage = numberKey * 10;
  const duration = endTime - startTime;
  const offset = duration * (seekPercentage / 100);
  return startTime + offset;
};

export const getFullscreenElement = (
  _containerElement: Element,
  _videoElement: Element,
) => {
  const containerElement = _containerElement as ElementWithFullscreen;
  const videoElement = _videoElement as ElementWithFullscreen;

  // In Safari on iOS the container element is not supporting fullscreen but the video element is.
  if (
    typeof containerElement.webkitSupportsFullscreen === 'boolean' ||
    typeof containerElement.webkitEnterFullscreen !== 'undefined' ||
    typeof containerElement.requestFullscreen !== 'undefined'
  ) {
    return containerElement;
  }

  if (
    typeof videoElement.webkitSupportsFullscreen === 'boolean' ||
    typeof videoElement.webkitEnterFullscreen !== 'undefined'
  ) {
    return videoElement;
  }

  return containerElement;
};

export const isFullscreenSupported = (_element: Element) => {
  const element = _element as ElementWithFullscreen;
  if (!window || !document) {
    return false;
  }

  try {
    return !!(
      document.fullscreenEnabled ||
      element.webkitSupportsFullscreen ||
      element.requestFullscreen ||
      element.webkitEnterFullscreen
    );
  } catch (error) {
    return false;
  }
};

export const isElementInFullscreen = (_element: Element) => {
  const element = _element as ElementWithFullscreen;
  if (typeof element.webkitDisplayingFullscreen === 'boolean') {
    return element.webkitDisplayingFullscreen;
  }

  return document?.fullscreenElement === element;
};

export const requestElementFullscreen = async (_element: Element) => {
  const element = _element as ElementWithFullscreen;

  if (element.requestFullscreen) {
    await element.requestFullscreen();
  } else if (element.webkitEnterFullscreen) {
    await element.webkitEnterFullscreen();
  }

  throw new Error('Fullscreen is not supported');
};

export const exitDocumentFullscreen = async (_element: Element | null) => {
  const element = _element as ElementWithFullscreen | null;

  if (document?.exitFullscreen) {
    await document.exitFullscreen();
  } else if (element?.webkitExitFullscreen) {
    await element.webkitExitFullscreen();
  }

  throw new Error('Fullscreen is not supported');
};

export const getVideoDuration = (video: HTMLVideoElement | null) => {
  if (!video) {
    return 0;
  }

  return isNaN(video.duration) ? 0 : video.duration;
};

export const getVideoAspectRatio = (video: VideoUploadProps['video']) => {
  const videoSources = video?.sources ?? [];
  const [videoSource] = videoSources;

  if (!videoSource || !videoSource.width || !videoSource.height) {
    return undefined;
  }

  return videoSource.width / videoSource.height;
};
